import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Send, MessageSquare, Users, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// Wave 2 — ad-hoc WhatsApp marketing campaigns on the marketing number.
// A campaign is an APPROVED, MARKETING-category template + its variables,
// sent to everyone who opted in to WhatsApp marketing and matches the
// segment. Not free text — WhatsApp business-initiated marketing must be a
// pre-approved template, so the admin picks one and fills its blanks.

interface Template {
  name: string
  language: string
  category: string
  status: string
  bodyText: string
  bodyVarCount: number
  hasUrlButton: boolean
  buttonUrlBase: string | null
  enabled: boolean
  automated: boolean
}

interface Campaign {
  id: string
  template_name: string
  template_lang: string
  variables: string | null
  url_button_param: string | null
  segment: string | null
  audience_size: number
  sent_count: number
  failed_count: number
  created_at: string
  sent_by_name: string | null
}

interface PreviewResult {
  mode: 'segment' | 'list'
  audience_size: number
  valid?: number
  invalid?: number
  duplicates?: number
  excluded?: number
  daily_cap?: number
  remaining_today?: number
}

const PLANS = [
  { value: 'all', label: 'All plans' },
  { value: 'free', label: 'Free plan' },
  { value: 'premium', label: 'Premium plan' },
]
const TIERS = [
  { value: 'all', label: 'All tiers' },
  { value: 'bottled', label: 'Bottled' },
  { value: 'aged', label: 'Aged' },
  { value: 'vintage', label: 'Vintage' },
]
const RECENCY = [
  { value: 'any', label: 'Any time' },
  { value: '30d', label: 'Ordered in last 30 days' },
  { value: '90d', label: 'Ordered in last 90 days' },
  { value: 'lapsed', label: 'Lapsed (no order in 180 days)' },
]

const CONFIRM_REQUIRED_AUDIENCE_THRESHOLD = 500

export default function WhatsAppCampaigns() {
  const { data: tmplData } = useQuery<{ templates: Template[]; configured: boolean }>({
    queryKey: ['wa-campaign-templates'],
    queryFn: () => api.get('/whatsapp-campaigns/templates').then(r => r.data),
  })
  const templates = useMemo(() => tmplData?.templates ?? [], [tmplData])

  // Automated-flow templates (welcome, gift, birthday, …) are hidden from the
  // picker by default — they fire from their own flows and shouldn't be
  // hand-blasted to a segment. Ops can reveal them with this toggle.
  const [showAutomated, setShowAutomated] = useState(false)
  const hasAutomated = templates.some(t => t.automated)
  const visibleTemplates = showAutomated ? templates : templates.filter(t => !t.automated)

  const [selectedKey, setSelectedKey] = useState('')
  const selected = templates.find(t => `${t.name}|${t.language}` === selectedKey) ?? null

  const [variables, setVariables] = useState<string[]>([])
  const [urlParam, setUrlParam] = useState('')
  // Link target picker — ops shouldn't have to remember raw paths. They pick
  // a target type; for a product they search by name (autocomplete).
  const [linkType, setLinkType] = useState<'app' | 'home' | 'product' | 'custom'>('app')
  const [productQuery, setProductQuery] = useState('')
  const [productResults, setProductResults] = useState<{ id: string; name: string }[]>([])
  const [chosenProduct, setChosenProduct] = useState<{ id: string; name: string } | null>(null)
  const [plan, setPlan] = useState('all')
  const [tier, setTier] = useState('all')
  const [recency, setRecency] = useState('any')
  // Audience mode: opted-in segment, or a pasted list of your own numbers.
  const [audienceMode, setAudienceMode] = useState<'segment' | 'list'>('segment')
  const [listNumbers, setListNumbers] = useState('')
  const [listNote, setListNote] = useState('')
  const [confirmLarge, setConfirmLarge] = useState(false)
  const [sent, setSent] = useState<{ sent: number; failed: number; cap_skipped?: number } | null>(null)

  // Reset the variable slots + link picker whenever the chosen template changes.
  useEffect(() => {
    setVariables(selected ? Array(selected.bodyVarCount).fill('') : [])
    setUrlParam('')
    setLinkType('app')
    setChosenProduct(null)
    setProductQuery('')
    setSent(null)
  }, [selectedKey, selected])

  // Derive the link suffix from the picker. 'custom' is driven by its own
  // input, so this effect leaves it alone.
  useEffect(() => {
    if (!selected?.hasUrlButton) return
    if (linkType === 'app') setUrlParam('get-app')
    else if (linkType === 'home') setUrlParam('en')
    else if (linkType === 'product') setUrlParam(chosenProduct ? `p/${chosenProduct.id}` : '')
  }, [linkType, chosenProduct, selected])

  // Product autocomplete — debounced search against the admin products list.
  useEffect(() => {
    if (linkType !== 'product' || productQuery.trim().length < 2) { setProductResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      api.get(`/products?search=${encodeURIComponent(productQuery.trim())}&limit=8`)
        .then(r => {
          if (cancelled) return
          const rows = ((r.data as { products?: { id: string; name: string }[] }).products ?? [])
          setProductResults(rows.map(p => ({ id: p.id, name: p.name })))
        })
        .catch(() => { if (!cancelled) setProductResults([]) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [productQuery, linkType])

  const segment = useMemo(() => ({ plan, tier, recency }), [plan, tier, recency])

  const [preview, setPreview] = useState<PreviewResult | null>(null)
  useEffect(() => {
    let cancelled = false
    const payload = audienceMode === 'list' ? { list_numbers: listNumbers } : { segment }
    const run = () => api.post('/whatsapp-campaigns/preview', payload)
      .then(r => { if (!cancelled) setPreview(r.data as PreviewResult) })
      .catch(() => { if (!cancelled) setPreview(null) })
    // Debounce while typing a list; segment changes resolve immediately.
    const t = setTimeout(run, audienceMode === 'list' ? 400 : 0)
    return () => { cancelled = true; clearTimeout(t) }
  }, [segment, audienceMode, listNumbers])

  const audience = preview?.audience_size ?? null
  useEffect(() => { setConfirmLarge(false) }, [segment, audienceMode, listNumbers])

  const overThreshold = (audience ?? 0) > CONFIRM_REQUIRED_AUDIENCE_THRESHOLD
  const varsFilled = variables.every(v => v.trim().length > 0)
  const urlOk = !selected?.hasUrlButton || urlParam.trim().length > 0
  const canSend = !!selected && selected.enabled && varsFilled && urlOk
    && (audience ?? 0) > 0 && (!overThreshold || confirmLarge)

  const send = useMutation({
    mutationFn: () => api.post('/whatsapp-campaigns/send', {
      template_name: selected!.name,
      template_lang: selected!.language,
      variables,
      ...(selected!.hasUrlButton ? { url_button_param: urlParam.trim() } : {}),
      ...(audienceMode === 'list'
        ? { list_numbers: listNumbers, list_note: listNote.trim() }
        : { segment }),
      ...(confirmLarge ? { confirm_large_broadcast: true } : {}),
    }),
    onSuccess: (r) => {
      const d = r.data as { sent: number; failed: number; cap_skipped?: number }
      setSent(d)
      setConfirmLarge(false)
      if (audienceMode === 'list') setListNumbers('')
      logs.refetch()
    },
  })

  const logs = useQuery<{ campaigns: Campaign[] }>({
    queryKey: ['wa-campaign-logs'],
    queryFn: () => api.get('/whatsapp-campaigns/logs').then(r => r.data),
  })

  return (
    <div className="p-6">
      <h1 className="text-lg font-semibold text-white mb-1">WhatsApp Campaigns</h1>
      <p className="text-sm text-gray-500 mb-6">
        Send an approved marketing template on the marketing number (+962 79 012 5452) to customers
        who opted in to WhatsApp marketing. Opt-outs and non-opted-in customers are never included.
      </p>

      {tmplData && !tmplData.configured && (
        <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-6">
          <AlertTriangle size={16} />
          WhatsApp is not fully configured (token or WABA ID missing). Template list is unavailable.
        </div>
      )}

      {/* Compose */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5 mb-6">
        <h2 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
          <MessageSquare size={15} className="text-[#C41E3A]" />
          Compose
        </h2>

        <div className="space-y-4">
          {/* Template picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-500 block">Marketing template</label>
              {hasAutomated && (
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAutomated}
                    onChange={e => {
                      setShowAutomated(e.target.checked)
                      // Clear the selection if we're hiding an automated one that's picked.
                      if (!e.target.checked && selected?.automated) setSelectedKey('')
                    }}
                    className="w-3.5 h-3.5 accent-[#C41E3A] cursor-pointer"
                  />
                  Show automated-flow templates
                </label>
              )}
            </div>
            <select
              value={selectedKey}
              onChange={e => setSelectedKey(e.target.value)}
              className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            >
              <option value="">Select an approved template…</option>
              {visibleTemplates.map(t => (
                <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                  {t.name} ({t.language}){t.automated ? ' — automated' : ''}{t.enabled ? '' : ' — not enabled'}
                </option>
              ))}
            </select>
            {templates.length === 0 && tmplData?.configured && (
              <p className="text-xs text-gray-600 mt-1">
                No approved marketing templates yet. Create one in the WhatsApp Manager and wait for approval.
              </p>
            )}
            {visibleTemplates.length === 0 && templates.length > 0 && !showAutomated && (
              <p className="text-xs text-gray-600 mt-1">
                Only automated-flow templates exist so far. Create a campaign template in the WhatsApp Manager,
                or tick "Show automated-flow templates" to use one of those (not usually what you want).
              </p>
            )}
          </div>

          {selected?.automated && (
            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                "{selected.name}" is an <strong>automated-flow</strong> template (it fires on its own from the
                {' '}welcome / gift / birthday flows). Blasting it to a segment is almost never what you want.
                Only continue if you're sure.
              </span>
            </div>
          )}

          {selected && !selected.enabled && (
            <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <AlertTriangle size={14} />
              This template is approved by Meta but not switched on. Turn "{selected.name}" on under
              <span className="text-gray-300"> Settings → WhatsApp</span> to send it.
            </div>
          )}

          {selected && (
            <>
              {/* Fill in the blanks — the message with inline inputs in place of
                  the template's {{n}} slots, so ops writes it in context. */}
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">
                  {selected.bodyVarCount > 0 ? 'Fill in the message' : 'Message'}
                </label>
                <div className="bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-3 text-sm text-gray-200 leading-9">
                  {selected.bodyText.split(/(\{\{\s*\d+\s*\}\})/g).map((part, idx) => {
                    const m = part.match(/\{\{\s*(\d+)\s*\}\}/)
                    if (!m) return <span key={idx} className="whitespace-pre-wrap">{part}</span>
                    const i = Number(m[1]) - 1
                    return (
                      <input
                        key={idx}
                        value={variables[i] ?? ''}
                        onChange={e => setVariables(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                        placeholder={`value ${i + 1}`}
                        className="inline-block align-baseline mx-1 min-w-[140px] bg-[#1d1d1d] border-b-2 border-[#C41E3A]/60 px-2 py-0.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] rounded-t"
                      />
                    )
                  })}
                </div>
                {selected.bodyVarCount > 0 && (
                  <p className="text-xs text-gray-600 mt-1">Type the highlighted parts. The rest is fixed by the approved template.</p>
                )}
              </div>

              {selected.hasUrlButton && (
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">
                    Button link <span className="text-gray-700">(where the button takes them)</span>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {([
                      ['app', 'Download app'],
                      ['product', 'A product'],
                      ['home', 'Home page'],
                      ['custom', 'Custom'],
                    ] as const).map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => { setLinkType(val); if (val === 'custom') setUrlParam('') }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          linkType === val ? 'bg-[#C41E3A]/20 text-[#C41E3A]' : 'bg-white/5 text-gray-400 hover:text-white'
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>

                  {linkType === 'product' && (
                    <div className="relative">
                      {chosenProduct ? (
                        <div className="flex items-center justify-between bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5">
                          <span className="text-sm text-white truncate">{chosenProduct.name}</span>
                          <button
                            type="button"
                            onClick={() => { setChosenProduct(null); setProductQuery('') }}
                            className="text-xs text-gray-500 hover:text-white ml-3 shrink-0"
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            value={productQuery}
                            onChange={e => setProductQuery(e.target.value)}
                            placeholder="Search products by name…"
                            className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A]"
                          />
                          {productResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg shadow-2xl max-h-56 overflow-y-auto">
                              {productResults.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => { setChosenProduct(p); setProductResults([]) }}
                                  className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-[#2a2a2a] hover:text-white transition-colors"
                                >
                                  {p.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {linkType === 'custom' && (
                    <input
                      value={urlParam}
                      onChange={e => setUrlParam(e.target.value)}
                      placeholder="path only, e.g. store/downtown"
                      className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A]"
                    />
                  )}

                  {(linkType === 'app' || linkType === 'home') && (
                    <p className="text-xs text-gray-600">
                      {linkType === 'app' ? 'Opens the app download page.' : 'Opens the DrinX home page.'}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Audience */}
          <div>
            <label className="text-xs text-gray-500 mb-1.5 block">Audience</label>
            <div className="flex gap-2 mb-3">
              {([['segment', 'Opted-in customers'], ['list', 'Paste a list']] as const).map(([val, lbl]) => (
                <button key={val} type="button" onClick={() => setAudienceMode(val)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    audienceMode === val ? 'bg-[#C41E3A]/20 text-[#C41E3A]' : 'bg-white/5 text-gray-400 hover:text-white'
                  }`}>
                  {lbl}
                </button>
              ))}
            </div>

            {audienceMode === 'segment' ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Plan</label>
                  <select value={plan} onChange={e => setPlan(e.target.value)}
                    className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
                    {PLANS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Tier</label>
                  <select value={tier} onChange={e => setTier(e.target.value)}
                    className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
                    {TIERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Order recency</label>
                  <select value={recency} onChange={e => setRecency(e.target.value)}
                    className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]">
                    {RECENCY.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <textarea value={listNumbers} onChange={e => setListNumbers(e.target.value)} rows={5}
                  placeholder="Paste phone numbers, one per line (07… or +9627…)"
                  className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] resize-y" />
                <input value={listNote} onChange={e => setListNote(e.target.value)}
                  placeholder="Note — where this list came from (saved with the campaign)"
                  className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A]" />
                <p className="text-xs text-gray-600">
                  Only use numbers you collected yourself (customers who gave you their number). Opted-out
                  and no-WhatsApp numbers are removed automatically.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
            <Users size={15} className="text-gray-500 shrink-0" />
            {audience === null
              ? 'Calculating audience…'
              : audienceMode === 'segment'
                ? <span><span className="text-white font-medium">{audience}</span> opted-in customer{audience === 1 ? '' : 's'} match this segment</span>
                : <span>
                    <span className="text-white font-medium">{audience}</span> number{audience === 1 ? '' : 's'} will receive it
                    {preview?.excluded ? ` · ${preview.excluded} opted-out/no-WhatsApp removed` : ''}
                    {preview?.invalid ? ` · ${preview.invalid} invalid` : ''}
                    {preview?.duplicates ? ` · ${preview.duplicates} duplicate` : ''}
                  </span>}
          </div>
          {preview?.remaining_today != null && (audience ?? 0) > preview.remaining_today && (
            <p className="text-xs text-amber-400">
              Daily cap: only {preview.remaining_today} can go out today. The rest are skipped, send again tomorrow.
            </p>
          )}

          {overThreshold && (
            <label className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 cursor-pointer">
              <input type="checkbox" checked={confirmLarge} onChange={e => setConfirmLarge(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-amber-400 cursor-pointer" />
              <span className="text-xs text-amber-200 leading-relaxed">
                <span className="font-semibold block mb-0.5">Large campaign, confirmation required</span>
                This will send a WhatsApp marketing message to {audience} recipients. Each marketing
                conversation is billable and the number has a daily limit. Tick to acknowledge.
              </span>
            </label>
          )}

          {sent && (
            <div className="text-green-400 text-sm bg-green-400/10 rounded-lg px-4 py-3">
              Campaign sent. {sent.sent} delivered to Meta{sent.failed > 0 ? `, ${sent.failed} failed` : ''}{sent.cap_skipped ? `. ${sent.cap_skipped} skipped by today's cap, send the rest tomorrow` : ''}.
            </div>
          )}
          {send.isError && (
            <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">
              {(send.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to send. Please try again.'}
            </div>
          )}

          <button
            onClick={() => send.mutate()}
            disabled={!canSend || send.isPending}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            <Send size={14} />
            {send.isPending ? 'Sending…' : 'Send Campaign'}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden overflow-x-auto">
        <div className="px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-medium text-gray-300">Sent Campaigns</h2>
        </div>
        {!logs.data?.campaigns?.length ? (
          <p className="px-5 py-6 text-sm text-gray-600">No campaigns sent yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                {['Template', 'Lang', 'Segment', 'Audience', 'Sent', 'Failed', 'By', 'Date'].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.data.campaigns.map(ca => {
                let seg: { mode?: string; plan?: string; tier?: string; recency?: string; note?: string } = {}
                try { seg = ca.segment ? JSON.parse(ca.segment) : {} } catch { /* ignore */ }
                const segLabel = seg.mode === 'list'
                  ? `Pasted list${seg.note ? ` (${seg.note})` : ''}`
                  : [seg.plan, seg.tier, seg.recency].filter(x => x && x !== 'all' && x !== 'any').join(' · ') || 'All opted-in'
                return (
                  <tr key={ca.id} className="border-b border-[#222]">
                    <td className="px-4 py-3 text-white font-medium">{ca.template_name}</td>
                    <td className="px-4 py-3 text-gray-400 uppercase">{ca.template_lang}</td>
                    <td className="px-4 py-3 text-gray-400">{segLabel}</td>
                    <td className="px-4 py-3 text-gray-400">{ca.audience_size}</td>
                    <td className="px-4 py-3 text-green-400">{ca.sent_count}</td>
                    <td className="px-4 py-3">{ca.failed_count > 0 ? <span className="text-red-400">{ca.failed_count}</span> : <span className="text-gray-700">0</span>}</td>
                    <td className="px-4 py-3 text-gray-400">{ca.sent_by_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(ca.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
